/** Rows at fractional pixel offsets can top out just below a ratio of 1, so a threshold of 1 may never fire. */
const FULLY_VISIBLE = 0.99;
const BATCH_MS = 400;

/**
 * Reports the IDs of elements once they have been fully on screen, batched into
 * one callback per short window. Elements carry their ID in `data-id`. Each ID is
 * reported at most once until `reset()`.
 */
export class SeenTracker {
  private readonly observer = new IntersectionObserver((entries) => this.onEntries(entries), {
    threshold: FULLY_VISIBLE,
  });
  private readonly reported = new Set<number>();
  private pending: number[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly onSeen: (ids: number[]) => void) {}

  observe(element: HTMLElement): void {
    if (!this.reported.has(Number(element.dataset['id']))) {
      this.observer.observe(element);
    }
  }

  /** Queues an ID as seen without waiting for it to scroll into view (e.g. it was opened). */
  report(id: number): void {
    if (this.reported.has(id)) return;
    this.reported.add(id);
    this.pending.push(id);
    this.timer ??= setTimeout(() => this.flush(), BATCH_MS);
  }

  hasReported(id: number): boolean {
    return this.reported.has(id);
  }

  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.pending.length === 0) return;
    const ids = this.pending;
    this.pending = [];
    this.onSeen(ids);
  }

  /** Sends what is pending, stops watching every element and forgets what was reported. */
  reset(): void {
    this.flush();
    this.observer.disconnect();
    this.reported.clear();
  }

  private onEntries(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < FULLY_VISIBLE) continue;
      this.observer.unobserve(entry.target);
      this.report(Number((entry.target as HTMLElement).dataset['id']));
    }
  }
}
